import { StorySlug } from "@/api/episodes-types";
import { useCreateStoryConfig } from "@/queries/story-configs/use-create-story-config";
import { useStoryConfigs } from "@/queries/story-configs/use-story-configs";
import { prettyStoryType } from "@/utils/pretty";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { AlertSnackbar, ApiErrorSnackbar } from "../ui/Snackbar";

export function CreateStoryConfig() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <CreateStoryConfigButton onClick={() => setOpen(true)} />
      <CreateStoryConfigDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function CreateStoryConfigButton({ onClick }: { onClick: () => void }) {
  const { data } = useStoryConfigs();
  const disabled =
    !data || data.items.length === Object.values(StorySlug).length;
  return (
    <Tooltip
      title={
        disabled ? "Configs for all story types have already been created" : ""
      }
      placement="left"
      disableInteractive
    >
      <Box>
        <Button variant={"contained"} onClick={onClick} disabled={disabled}>
          New story config
        </Button>
      </Box>
    </Tooltip>
  );
}

function CreateStoryConfigDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  // undefined means input not touched
  const [storyType, setStoryType] = useState<StorySlug>();

  const { data } = useStoryConfigs();

  const {
    mutate: createStoryConfig,
    error,
    isPending,
    isSuccess,
  } = useCreateStoryConfig();

  function handleClose() {
    onClose();
    setStoryType(undefined);
  }

  function handleCreate() {
    if (!storyType) {
      return;
    }
    createStoryConfig(
      { data: { storyType } },
      {
        onSuccess: (created) => {
          navigate(`/story-configs/${created.id}`);
          handleClose();
        },
      },
    );
  }

  const availableStoryTypes = data
    ? Object.values(StorySlug).filter(
        (slug) => !data.items.some((config) => config.storyType === slug),
      )
    : [];

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
        <DialogTitle>Create New Story Config</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={0.75}>
            <TextField
              select
              label={"Story type"}
              value={storyType || ""}
              onChange={(e) => setStoryType(e.target.value as StorySlug)}
            >
              {availableStoryTypes.map((slug) => (
                <MenuItem key={slug} value={slug}>
                  {prettyStoryType(slug)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Tooltip
            title={!storyType ? "Select a story type to create" : ""}
            placement="top"
            disableInteractive
          >
            <Box>
              <Button
                variant={"contained"}
                onClick={handleCreate}
                loading={isPending}
                disabled={!storyType}
              >
                Create
              </Button>
            </Box>
          </Tooltip>
        </DialogActions>
      </Dialog>
      <ApiErrorSnackbar error={error} />
      <AlertSnackbar severity="success">
        {isSuccess ? "Story config created successfully" : undefined}
      </AlertSnackbar>
    </>
  );
}
